(ns frontend.view.task.webentry-resolution.contacts
  (:require
   [basic-components.inputs.footprint :refer [editor]]
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.constraints :as constraints]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.components.drag-layout :as drag-layout]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.task.webentry-resolution.ai-components :as ai-components]
   [frontend.view.task.webentry-resolution.components :as components]
   [frontend.view.task.webentry-resolution.db]
   [frontend.view.task.webentry-resolution.events :as events]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.util :as uu]))

(defn info-bar
  [{:keys [message attachments] :as signals}]
  (let [message @message
        submitted-attachments @attachments]
    [:div
     {:class "p-3"}
     (when-not (string/blank? message)
       [components/message-info message])
     (when-not (empty? submitted-attachments)
       [components/attachments-section signals :submitted-attachments
        (loc/tr :submitted-attachments-n [(count submitted-attachments)]) submitted-attachments])]))

(defn association-selector
  [signals local-state match association read-only?]
  (let [options (events/association-options signals)
        value (get association :entity-association/type)]
    [:div
     ^{:key (str "association-" (:db/id association))}
     [editor
      {:editor/type :editor.type/select
       :value       value
       :clearable   false
       :value-type  :keyword
       :read-only?  (or read-only? (:association-processing? @local-state))
       :class-name  "association-type__select"
       :options     options
       :on-blur!    (fn [_e new-value clear-cb!]
                      (if association
                        (events/update-association! signals
                                                    (:db/id association)
                                                    new-value
                                                    clear-cb!)
                        (events/create-asset-association! signals
                                                          local-state
                                                          (:db/id match)
                                                          new-value
                                                          clear-cb!)))}]]))

(defn new-association-selector
  [signals local-state]
  (let [options (events/association-options signals)
        {:keys [association-type processing?]} @local-state]
    [:div
     [editor
      {:editor/type :editor.type/select
       :value       association-type
       :read-only?  processing?
       :clearable   false
       :value-type  :keyword
       :class-name  "association-type__select"
       :options     options
       :on-blur!    (fn [_e new-value clear-cb!]
                      (swap! local-state assoc :association-type new-value)
                      (clear-cb!))}]]))

(defn association-list
  [{:keys [completed?] :as signals} local-state match associations]
  (let [completed? @completed?]
    [:div
     (doall
      (for [association associations]
        ^{:key (str "association-" (:db/id association))}
        [association-selector signals local-state match association completed?]))]))

(defn association-view
  [{:keys [resolved-asset completed?] :as signals} local-state person match?]
  (let [asset @resolved-asset
        completed? @completed?
        match (when match?
                (events/matching-contact signals person))
        associations (when match?
                       (fu/entities-associations (:db/id match) (:db/id asset)))]
    [:div
     {:class "px-3"}
     (cond
       completed?
       [:div
        [:div
         (if (> (count associations) 1)
           (loc/tr :associations-with-the-asset)
           (loc/tr :association-with-the-asset))]
        [association-list signals local-state match associations]]
       (not match?)
       [:div
        [:div
         {:class "flex gap-2"}
         (loc/tr :association-with-the-asset)
         [:i.fas.fa-asterisk.text-danger-light-text.fa-xs]]
        [new-association-selector signals local-state]]
       (> (count associations) 1)
       [:div
        [:div
         (loc/tr :associations-with-the-asset)]
        [association-list signals local-state match associations]]
       :else
       [:div
        [:div
         {:class "flex gap-2"}
         (loc/tr :association-with-the-asset)
         [:i.fas.fa-asterisk.input-editor__label-mandatory.ml-1.fa-xs]]
        [:div
         {:class "flex"}
         [:div
          {:class-name "w-2/4"}
          [:div
           [association-selector signals local-state match (first associations)]]]]])]))

;;note: leaving these in case needed for later updates:
#_(defn contact-actions-view
    [{:keys [resolved-asset completed?] :as signals} local-state person match?]
    (let [asset @resolved-asset
          completed? @completed?
          {:keys [processing? association-type]} @local-state
          association-selected? (when-not match?
                                  (not (nil? association-type)))
          match (events/matching-contact signals person)
          deletable? match?
          type-lc (-> :entity.type/person
                      fu/entity-type-name
                      str
                      string/lower-case)]
      [:div
       (when asset
         [association-view signals local-state person match?])
       (when-not completed?
         [:div
          (when-not match?
            [button/primary-solid
             {:disabled (boolean (or processing?
                                     (not association-selected?)))
              :on-click (fn [_]
                          (when association-selected?
                            (events/create-person signals person association-type false)))}
             (loc/tr :create-new-entity [type-lc])])
          (when deletable?
            [button/danger-solid
             {:disabled (boolean processing?)
              :on-click (fn [_]
                          (events/disable-contact! signals match))}
             [:i.fal.fa-trash]
             (loc/tr :delete-entity [type-lc])])])]))

(defn updater-buttons
  [{:keys [resolved-asset contact-updater-fields] :as signals} person-state ord]
  (let [contact-updater-fields @contact-updater-fields
        updater-fields (get contact-updater-fields ord)
        asset @resolved-asset
        {:keys [update-processing?]} @person-state
        type-lc (->> (loc/tr :entity.type/person)
                     string/lower-case)
        match (events/matching-contact signals {:ord ord})
        no-info? (and (empty? updater-fields)
                      (not asset))
        associations (fu/entities-associations (:db/id match) (:db/id asset))
        disabled? (and asset
                       (empty? associations))]
    [:div
     {:class "inline-flex flex-col gap-2 pt-3 px-3"}
     [button/primary-solid
      {:class     "me-3"
       :disabled? (boolean (or update-processing?
                               disabled?))
       :on-click  (fn [_]
                    (when-not disabled?
                      (events/update-contact! signals person-state ord)))}
      (cond
        update-processing?
        (loc/tr :processing)
        no-info?
        (loc/tr :continue)
        :else
        (loc/tr :entity-updater-update-button [type-lc]))]]))

(defn updater-actions
  [signals updater-state ord]
  (let [select-action! (fn [_ kw clear-state!]
                         (events/select-contact-update-action! signals updater-state ord kw)
                         (clear-state!))]
    ;;set start value for selections (update empty fields with submitted data):
    (events/select-contact-update-action! signals updater-state ord :updater-update-empty)
    (fn [_signals updater-state _ord]
      (let [selected-action (-> @updater-state
                                :action)
            options (->> [:updater-update-empty :updater-keep-current
                          :updater-replace-all :updater-customize]
                         (map (fn [kw]
                                {:key   kw
                                 :label (loc/tr kw)})))]
        [:div
         {:class "py-2 *:flex *:flex-col *:gap-2"}
         [editor
          {:editor/type :editor.type/radio
           :value       selected-action
           :options     options
           :on-blur!    select-action!}]]))))

(defn updater-table
  [{:keys [submitted-contacts contact-updater-fields] :as signals} updater-state ord]
  (let [submitted-contact (-> @submitted-contacts
                              (get ord)
                              (dissoc :person/universal-email-address))
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        matching-contact (events/matching-contact signals {:ord ord})
        updater-fields (get @contact-updater-fields ord)
        submitted-selected (-> @updater-state
                               :submitted-selected
                               set)]
    [:div
     {:class "my-4"}
     ;;TODO if no updater-fields, do/show what?
     [:div
      {:class "py-2 px-3 bg-base-regular"}
      [:div
       {:class-name "grid grid-cols-2 gap-4"}
       [:div (loc/tr :submitted-value)]
       [:div (loc/tr :current-value-in-db)]]]
     [:div
      (doall
       (for [k updater-fields
             :let [field-schema (registry/get-field-schema tenant-schema k)
                   field-name (attribute/attribute-label tenant-schema k)
                   submitted-value
                   (fu/display-data tenant-schema tenant k field-schema submitted-contact)
                   current-value
                   (fu/display-data tenant-schema tenant k field-schema matching-contact)
                   submitted? (contains? submitted-selected k)]]
         ^{:key (str "contact-updater-table-field-" k "-" ord)}
         [:di
          [:div
           {:class "p-4 text-center text-bold"}
           field-name]
          [:div
           {:class "px-4"}
           [:div
            {:class-name "grid grid-cols-2 gap-3"}
            [:div
             [:div
              {:class-name (uu/cm "flex gap-2 items-center py-2 px-4 border border-solid border-base-light-border-paler rounded"
                                  (when (contains? submitted-selected k)
                                    "bg-primary-light border-primary-light-border"))
               :on-click   (fn [_]
                             (events/select-submitted-contact-field! signals updater-state ord k))}
              (if submitted?
                [:i.fas.fa-check-circle]
                [:i.fal.fa-circle])
              [components/truncated-text
               submitted-value 50 false]]]
            [:div
             [:div
              {:class-name (uu/cm "flex gap-2 items-center py-2 px-4 border border-solid border-base-light-border-paler rounded"
                                  (when-not (contains? submitted-selected k)
                                    "bg-primary-light border-primary-light-border"))
               :on-click   (fn [_]
                             (events/select-current-contact-field! signals updater-state ord k))}
              (if submitted?
                [:i.fal.fa-circle]
                [:i.fas.fa-check-circle])
              [components/truncated-text
               current-value 50 false]]]]]]))]]))

(defn match-contact-view
  [{:keys [state _resolved-asset contact-updater-fields]} person]
  (let [ord (:ord person)
        person-state (r/cursor state [:persons ord])
        updater-state (r/cursor person-state [:updater])]
    (fn [{:keys [resolved-asset] :as signals} person]
      (let [{:keys [ord]} person
            person (cu/remove-empty-vals person)
            match (events/matching-contact signals person)
            icon (entity/entity-type-icon :entity.type/person)
            contact-updater-fields @contact-updater-fields
            updater-fields (get contact-updater-fields ord)
            no-further-info? (empty? updater-fields)
            asset @resolved-asset]
        ^{:key (str "contact-view-" ord "-" (:db/id match))}
        [:div
         [alert/warning
          {}
          [:div
           (if no-further-info?
             (loc/tr :contact-match-no-further-info)
             (loc/tr :contact-match-info))]]

         [:div
          {:class "mt-4 border border-solid border-base-light-border-paler rounded"}
          [:div
           {:class "flex gap-2 p-3 border-b border-solid border-base-light-border-paler text-semibold"}
           [:i {:class-name (fu/cs "fal" icon)}]
           (entity/display-string match :long)]

          [:div
           {:class "p-3"}
           (when no-further-info?
             [:div
              {:class "mb-4"}
              (loc/tr :no-further-contact-info)])

           (when-not no-further-info?
             [:div
              [:div
               {:class "text-bold"}
               (loc/tr :contact-updater-title)]])

           (when-not no-further-info?
             [:div
              [updater-actions signals updater-state ord]
              [updater-table signals updater-state ord]])

           (when asset
             [association-view signals person-state person true])

           [updater-buttons signals person-state ord]]]]))))

(defn resolved-contact-view
  [{:keys [_resolved-asset resolved-contacts-data]} _person]
  (let [local-state (r/atom {:hide-info? false})
        hide-info! (fn [_]
                     (swap! local-state assoc :hide-info? true))]
    (fn [{:keys [resolved-asset] :as signals} person]
      (let [{:keys [ord]} person
            person (cu/remove-empty-vals person)
            match (events/resolved-contact signals person)
            tenant @session/tenant
            tenant-schema @session/tenant-schema
            contact-id (:db/id match)
            contact-name (entity/display-string match)
            schema (events/get-contact-summary-schema signals)
            resolved-contact-data (get @resolved-contacts-data ord)
            resolve-type (get resolved-contact-data :resolved-status)
            type-name (loc/tr :entity.type/person)
            type-lc (-> type-name
                        string/lower-case)
            info-title (case resolve-type
                         :created
                         (loc/tr :resolved-entity-created-info-title [type-name contact-name])
                         :updated
                         (loc/tr :resolved-entity-updated-info-title [contact-name])
                         nil)
            info-text (case resolve-type
                        :created
                        (loc/tr :resolved-asset-created-info [type-lc contact-name])
                        :updated
                        (loc/tr :resolved-asset-updated-info [contact-name])
                        nil)
            asset @resolved-asset
            asset-name (when asset
                         (entity/display-string asset))
            associations (when asset
                           (fu/entities-associations (:db/id match) (:db/id asset)))]
        ^{:key (str "r-contact-view-" ord "-" (:db/id person))}
        [:div
         (when (and (not (-> @local-state
                             :hide-info?))
                    info-title
                    info-text)
           [alert/info
            {}
            [:div
             {:class "font-bold"}
             info-title]
            [:div
             info-text]
            [:div
             {:class    "absolute top-3 right-3"
              :on-click hide-info!}
             [:i.fal.fa-times]]])

         (when-not (empty? associations)
           [:div
            [:div
             {:class "mt-3"}
             (if (> (count associations) 1)
               [:strong
                {:class "block text-sm mb-2 text-bold"}
                (loc/tr :associations-with [asset-name])]
               [:strong
                {:class "block text-sm mb-2 text-bold"}
                (loc/tr :association-with [asset-name])])]
            (for [{:keys [db/id]} associations]
              ^{:key (str "r-association-" id)}
              [:div (loc/tr type)])])

         [:table
          {:class "mt-4 w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
          [:thead
           [:tr
            [:th
             {:class    "py-2 px-3 w-[24%] bg-base-regular"
              :col-span "2"}
             (loc/tr :resolved-entity-details [type-name])]]]
          [:tbody
           (doall
            (for [[attribute field-schema] schema
                  :let [label (attribute/attribute-label tenant-schema attribute)
                        value (fu/get-value tenant tenant-schema match attribute)
                        display-value
                        (fu/display-data tenant-schema tenant attribute field-schema match)
                        data-type (:field/data-type field-schema)
                        text? (some #{data-type}
                                    [:data.type/text-simple
                                     :data.type/text-multi
                                     :data.type/url-multi
                                     :data.type/text-multiline
                                     :data.type/text-richtext])]
                  :when (not (cu/blank-value? value))]
              ^{:key (str "contact-" contact-id "-" attribute)}
              [:tr
               [:td
                {:class "py-2 px-3 w-[24%] align-middle text-semibold border-b border-solid border-base-light-border-paler"}
                label]
               [:td
                {:class "py-2 px-3 w-[24%] align-middle text-semibold border-b border-solid border-base-light-border-paler"}
                (if text?
                  [components/truncated-text display-value 100 false]
                  display-value)]]))]]]))))

(defn validate-contact-field!
  [{:keys [draft-state error-state]} field-schema updated-attribute new-value]
  (let [field-constraints (:field/constraints field-schema)
        errors (constraints/validate field-constraints
                                     nil
                                     new-value
                                     @draft-state
                                     (partial session/unique-check
                                              false
                                              (partial routing/open-entity))
                                     loc/tr)
        ;;skip email validation if value is empty
        errors (if (and (= :person/work-email-address updated-attribute)
                        (or (nil? new-value)
                            (= "" new-value)))
                 nil
                 errors)]
    (if errors
      (swap! error-state assoc updated-attribute errors)
      (swap! error-state dissoc updated-attribute))
    errors))

(defn validate-all!
  [{:keys [contact-creator-schema]} draft-state error-state]
  (let [schema @contact-creator-schema
        vals @draft-state]
    (doseq [[attribute field-schema] schema]
      (validate-contact-field! {:draft-state draft-state
                                :error-state error-state}
                               field-schema
                               attribute
                               (get vals attribute)))))

(defn non-match-contact-view
  [{:keys [state contact-creator-schema] :as signals} person]
  (let [ord (:ord person)
        person-state (r/cursor state [:persons ord])
        draft-state (r/cursor person-state [:draft])
        error-state (r/cursor person-state [:errors])
        validate-field! (partial validate-contact-field!
                                 {:draft-state draft-state
                                  :error-state error-state})]
    (reset! draft-state person)
    (validate-all! signals draft-state error-state)
    (fn [{:keys [resolved-asset] :as signals} person]
      (let [{:keys [ord]} person
            person (cu/remove-empty-vals person)
            mandatory-fields [:person/universal-email-address]
            contact-creator-schema (->> @contact-creator-schema
                                        (filter (fn [[k _v]]
                                                  (let [submitted-value (get person k)]
                                                    (or (some #{k} mandatory-fields)
                                                        (not (cu/blank-value? submitted-value)))))))
            mandatory-fields [:person/universal-email-address]
            asset @resolved-asset
            {:keys [processing? association-type]} @person-state
            association-selected? (not (nil? association-type))
            errors @error-state
            button-disabled? (or (and asset
                                      (not association-selected?))
                                 (seq errors))
            type-lc (-> :entity.type/person
                        loc/tr
                        str
                        string/lower-case)]
        ^{:key (str "non-match-contact-view-" ord)}
        [:div
         [:div
          {:class "border border-solid border-base-light-border-paler rounded"}
          [:div
           {:class "p-3 border-b border-solid border-base-light-border-paler"}
           (loc/tr :submitted-information)]
          [:div
           {:class "flex flex-col gap-4 p-3"}
           (doall
            (for [[attribute field-schema] contact-creator-schema
                  :let [value      (get @draft-state attribute)
                        mandatory? (boolean (some #{attribute} mandatory-fields))]]
              ^{:key (str "contact-creator-" ord "-" attribute)}
              [components/creator-field
               {:attribute    attribute
                :field-schema field-schema
                :value        value
                :label        (or (->> field-schema
                                       :field/translations
                                       :en)
                                  (loc/tr attribute)
                                  (when-let [fs (registry/get-field-schema
                                                 @session/tenant-schema
                                                 attribute)]
                                    (loc/field-name attribute fs)))
                :mandatory?   mandatory?
                :error        (get @error-state attribute)
                :containing-entity @draft-state
                :validate!    (fn [attr v] (validate-field! field-schema attr v))
                :save!        (fn [attr v] (swap! draft-state assoc attr v))
                :set-error!   (fn [attr err] (swap! error-state assoc attr err))}]))
           (when asset
             [:div
              {:class "py-2"}
              [:div
               {:class "mb-0 flex gap-2 text-sm font-semibold"}
               (loc/tr :association-with-the-asset)
               [:i.fas.fa-asterisk.text-danger-light-text.text-xxs]]
              [:div
               {:class-name "flex"}
               [:div
                {:class-name "w-2/4"}
                [:div
                 [new-association-selector signals person-state]]]]])

           [button/sm-primary-solid
            {:class     (fu/cs "mt-3 self-start")
             :disabled? (boolean (or processing?
                                     button-disabled?))
             :on-click  (fn [_]
                          (when-not button-disabled?
                            (events/create-person signals draft-state association-type false)))}
            (if processing?
              (loc/tr :processing)
              (loc/tr :create-new-entity [type-lc]))]]]]))))

;;old version kept for reference, remove when no longer needed
#_(defn contact-view
    [{:keys [state resolved-asset completed?] :as signals} person match?]
    (let [ord (:ord person)
          local-state (r/cursor state [:persons ord])]
      (fn [{:keys [resolved-asset completed?] :as signals} person match?]
        (let [{:keys [person/universal-email-address person/avatar-url]} person
              person (cu/remove-empty-vals person)
              completed? @completed?
              match (when match?
                      (events/matching-contact signals person))
              asset @resolved-asset]
          [:div
           [:div
            {:class-name "grid grid-cols-10 gap-4 px-4"}
            [:div
             {:class-name "col-span-6"}
             [contact-info-view signals person match?]]
            [:div
             {:class-name "col-span-4"}
             [contact-actions-view signals local-state person match?]]]]))))

(defn processing-view
  []
  [:div
   ;;TODO styling
   (loc/tr :processing)])

(defn contact-view
  [{:keys [state] :as signals} contact]
  (let [{:keys [resolved? match? ord]} contact
        person-state (r/cursor state [:persons ord])
        processing? (:processing? @person-state)]
    (cond
      processing?
      [processing-view]
      resolved?
      [resolved-contact-view signals contact]
      match?
      [:div
       {:class "border border-solid border-base-light-border-paler rounded"}
       [:div
        {:class "p-3 border-b border-solid border-base-light-border-paler text-semibold"}
        (loc/tr :contact)]
       [:div
        {:class "p-3"}
        [match-contact-view signals contact]]]
      :else
      [:div
       {:class "border border-solid border-base-light-border-paler rounded"}
       [:div
        {:class "p-3 border-b border-solid border-base-light-border-paler text-semibold"}
        (loc/tr :contact)]
       [:div
        {:class "p-3"}
        [non-match-contact-view signals contact]]])))

(defn no-contact-info-view
  []
  [:div
   {:class "my-5 text-center"}
   [:img
    {:class "max-w-[150px]"
     :src   "/img/no-data-created.svg"}]
   [:p
    {:class "mt-4"}
    [:strong
     {:class "font-bold"}
     (loc/tr :no-webform-contact)]]])

(defn main-contacts-view
  [{:keys [submitted-contacts contacts-list has-ai-suggestions? ai-contacts ai-status]
    :as   signals}]
  (let [submitted-contacts @submitted-contacts
        contacts @contacts-list
        has-ai? @has-ai-suggestions?
        ai-contact-count (count @ai-contacts)]
    [:div
     {:class "container mt-4"}
     [:div
      {:class "flex justify-center"}
      [components/state-indicator signals]]
     [:div
      {:class "container mt-4 mx-auto max-[1024px]"}
      [ai-components/ai-unavailable-banner {:status @ai-status}]]
     ;; Show AI suggestions banner if available
     (when (and has-ai? (pos? ai-contact-count))
       [:div
        {:class "container mt-4 mx-auto max-[1024px]"}
        [ai-components/ai-suggestions-banner
         {:contact-count ai-contact-count}]])
     [:div
      {:class "container mt-4 mx-auto max-[1024px]"}
      (if (empty? submitted-contacts)
        [no-contact-info-view]
        [:div
         (doall
          (for [contact contacts]
            ^{:key (str "contact-view-" (:ord contact))}
            [contact-view signals contact]))])]]))

(defn view
  [drag-state {:keys [] :as signals}]
  (let [top-bottom? false
        view-info (events/contacts-view-info signals)
        info? (:show-info? view-info)]
    (if info?
      [(if top-bottom?
         drag-layout/top-bottom
         drag-layout/left-right)
       drag-state
       "webentry-resolution-layout"
       [info-bar signals]
       [main-contacts-view signals]]
      [main-contacts-view signals])))
